import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, Button, Checkbox,
  Divider, TableContainer, Table, TableHead, TableRow,
  TableCell, TableBody, Box, Typography, IconButton, TextField
} from "@mui/material";
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import api from '../Api';
import { addDaysToDateString, getAppDateString } from "../utils/date";
import { groupsFromAccounts, groupMatchesSelection, exportUnscannedCsv } from "../utils/scanGroups";

const NotScannedDialog = ({ open, onClose, accounts = [], initialGroup = "" }) => {
    const [selectedNsAccount, setSelectedNsAccount] = useState("");
    const [selectedGroup, setSelectedGroup] = useState("");
    const [nsOrderData, setNsOrderData] = useState([]);
    const [showAllAccounts, setShowAllAccounts] = useState(false);
    const [selectedDate, setSelectedDate] = useState(() => getAppDateString());
    const [picked, setPicked] = useState(() => new Set());
    const [openingPdf, setOpeningPdf] = useState(false);
    const [directScanning, setDirectScanning] = useState(false);
    const [directScanOpen, setDirectScanOpen] = useState(false);
    const [directScanRemarks, setDirectScanRemarks] = useState("");

    const groupOptions = groupsFromAccounts(accounts);

    // When "all" is loaded and a group is selected, filter to that group's accounts.
    const displayedData = (showAllAccounts && selectedGroup)
      ? nsOrderData.filter((g) => groupMatchesSelection(g.account_name, selectedGroup))
      : nsOrderData;

    const rowKey = (accountName, trackingId) => `${accountName || ""}\u001f${trackingId || ""}`;

    // Flat list of visible rows (for select-all, open selected, and direct scan).
    const visibleRows = useMemo(() => {
      const rows = showAllAccounts
        ? displayedData.flatMap(g => (g.orders || []).map(o => ({
            tracking_id: o.tracking_id,
            order_id: o.order_id,
            order_item_id: o.order_item_id,
            sku: o.sku,
            account_name: g.account_name,
          })))
        : displayedData.map(o => ({
            tracking_id: o.tracking_id,
            order_id: o.order_id,
            order_item_id: o.order_item_id,
            sku: o.sku,
            account_name: selectedNsAccount,
          }));
      return rows.filter(row => row.tracking_id && row.account_name);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [displayedData, selectedNsAccount, showAllAccounts]);

    const visibleKeys = visibleRows.map(row => rowKey(row.account_name, row.tracking_id));
    const pickedRows = visibleRows.filter(row => picked.has(rowKey(row.account_name, row.tracking_id)));

    const labelPdfErrorMessage = async (err) => {
      let msg = "Failed to open labels.";
      try {
        const payload = JSON.parse(await err.response.data.text());
        msg = payload.error || msg;
        if (Array.isArray(payload.fallback) && payload.fallback.length && !/fallback/i.test(msg)) {
          const detail = payload.fallback
            .map((row) => {
              const platform = row.platform || "fallback";
              if (!row.attempted) return `${platform} fallback skipped: ${row.reason || "not applicable"}`;
              const count = row.printed ?? row.recovered ?? row.linked ?? 0;
              return `${platform} fallback ${row.ok ? `ok (${count})` : `failed${row.error ? `: ${row.error}` : ""}`}`;
            })
            .join("; ");
          if (detail) msg += ` ${detail}`;
        }
      } catch (_) {}
      return msg;
    };

    // Reset selection whenever the shown data changes.
    useEffect(() => { setPicked(new Set()); }, [nsOrderData, selectedGroup]);

    const allChecked = visibleKeys.length > 0 && visibleKeys.every(key => picked.has(key));
    const toggleAll = () => setPicked(allChecked ? new Set() : new Set(visibleKeys));
    const toggleOne = (accountName, trackingId) => setPicked(prev => {
      const next = new Set(prev);
      const key = rowKey(accountName, trackingId);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

    const openSelectedPdfs = async () => {
      const ids = [...new Set(pickedRows.map(row => row.tracking_id))];
      if (!ids.length) { alert("Tick at least one order to open its label."); return; }
      setOpeningPdf(true);
      try {
        const resp = await api.post("/api/label-pdf",
          {
            tracking_ids: ids,
            date: selectedDate,
            items: pickedRows.map(row => ({
              id: row.tracking_id,
              tracking_id: row.tracking_id,
              account: row.account_name,
              order_id: row.order_id || "",
              order_item_id: row.order_item_id || "",
              sku: row.sku || "",
            })),
          },
          { responseType: "blob" });
        const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
        window.open(url, "_blank");
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      } catch (err) {
        const msg = await labelPdfErrorMessage(err);
        alert("❌ " + msg);
      } finally {
        setOpeningPdf(false);
      }
    };

    const refreshCurrentView = async () => {
      if (showAllAccounts) {
        await fetchUnscannedOrders("all");
      } else if (selectedNsAccount) {
        await fetchUnscannedOrders(selectedNsAccount);
      }
    };

    const directScanSelected = async () => {
      const scans = pickedRows.map(row => ({
        tracking_id: row.tracking_id,
        account_name: row.account_name,
      }));
      if (!scans.length) {
        alert("Tick at least one order to scan direct.");
        return;
      }
      setDirectScanRemarks("");
      setDirectScanOpen(true);
    };

    const confirmDirectScan = async () => {
      const scans = pickedRows.map(row => ({
        tracking_id: row.tracking_id,
        account_name: row.account_name,
      }));
      if (!scans.length) return;

      setDirectScanning(true);
      try {
        const response = await api.post("/api/admin/direct-scan-selected", {
          date: selectedDate,
          scans,
          remarks: directScanRemarks,
        });
        setDirectScanOpen(false);
        const remarkNote = response.data?.remarks_saved ? ` Remark saved for ${response.data.remarks_saved} account(s).` : "";
        alert(`✅ ${response.data?.message || "Direct scan completed."}${remarkNote}`);
        setPicked(new Set());
        await refreshCurrentView();
      } catch (err) {
        alert("❌ " + (err.response?.data?.message || err.response?.data?.error || "Direct scan failed."));
      } finally {
        setDirectScanning(false);
      }
    };

    const fetchUnscannedOrders = async (accountName) => {
        try {
          const endpoint = accountName === "all"
            ? `/api/unscanned-orders/all?date=${selectedDate}`
            : `/api/unscanned-orders/${encodeURIComponent(accountName.trim())}?date=${selectedDate}`;

          const response = await api.get(endpoint);
          const data = response.data;

          if (Array.isArray(data)) {
            setNsOrderData(data);
          } else {
            setNsOrderData([]);
          }
        } catch (error) {
          console.error("❌ Error fetching unscanned orders:", error);
          alert("❌ Failed to fetch unscanned orders.");
        }
      };

    // Fetch "all" then keep only the chosen group (grouped/all view).
    const fetchGroup = (group) => {
      setSelectedNsAccount("");
      setSelectedGroup(group);
      setShowAllAccounts(true);
      fetchUnscannedOrders("all");
    };

    // Opened from a summary "remaining" click → auto-load that group.
    useEffect(() => {
      if (open && initialGroup) {
        fetchGroup(initialGroup);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, initialGroup, selectedDate]);


  const handleDateChange = (days) => {
    if (!selectedDate || isNaN(new Date(selectedDate))) {
      console.error("❌ Invalid selectedDate:", selectedDate);
      return;
    }
  
    setSelectedDate(addDaysToDateString(selectedDate, days));
  };
  
  

  return (
    <>
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
      <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap">
      <Typography
        variant="h4"
        sx={{
            fontWeight: 'bold',
            fontFamily: `'Caveat', 'Patrick Hand', 'Comic Sans MS', cursive`,
            fontStyle: 'normal',
            textAlign: 'center',
            color: '#333'
        }}
        >
        🕵️‍♂️ Non-Scanned Orders
        </Typography>
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 1, mb: 2 }}>
            <IconButton
                onClick={() => {
                setSelectedDate(addDaysToDateString(selectedDate, -1));
                }}
            >
                <ChevronLeftIcon />
            </IconButton>

            <TextField
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                sx={{ width: 150 }}
                size="small"
            />

            <IconButton
                onClick={() => {
                setSelectedDate(addDaysToDateString(selectedDate, 1));
                }}
            >
                <ChevronRightIcon />
            </IconButton>
            

            <Typography variant="body2" sx={{ color: "#666", mt: { xs: 1, sm: 0 } }}>
                📦 {showAllAccounts
                ? displayedData.reduce((acc, group) => acc + (group.orders?.length || 0), 0)
                : displayedData.length} entries;
                {" "}
                {showAllAccounts
                ? new Set(displayedData.flatMap(g => g.orders?.map(o => o.tracking_id) || [])).size
                : new Set(displayedData.map(o => o.tracking_id)).size} orders
            </Typography>
            </Box>
        </Box>
        </DialogTitle>



      <DialogContent>
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <FormControl margin="normal" sx={{ flex: 1, minWidth: 200 }}>
            <InputLabel>Select Account</InputLabel>
            <Select
              value={selectedNsAccount}
              label="Select Account"
              onChange={(e) => { setSelectedNsAccount(e.target.value); setSelectedGroup(""); }}
            >
              <MenuItem value="">-- Select Account --</MenuItem>
              {Array.isArray(accounts) && accounts.map((account) => (
                <MenuItem key={account.id} value={account.name}>{account.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl margin="normal" sx={{ minWidth: 160 }}>
            <InputLabel>Group</InputLabel>
            <Select
              value={selectedGroup}
              label="Group"
              onChange={(e) => {
                const g = e.target.value;
                if (g) fetchGroup(g);
                else { setSelectedGroup(""); setShowAllAccounts(false); setNsOrderData([]); }
              }}
            >
              <MenuItem value="">-- All Groups --</MenuItem>
              {groupOptions.map((g) => (
                <MenuItem key={g} value={g}>{g}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Compact action row: fetch + export + open-selected all on one line */}
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center", mb: 1 }}>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => {
              if (selectedNsAccount) {
                setShowAllAccounts(false);
                setSelectedGroup("");
                fetchUnscannedOrders(selectedNsAccount);
              } else {
                alert("Please select an account to fetch orders.");
              }
            }}
          >
            Fetch Orders
          </Button>

          <Button
            variant="contained"
            color="secondary"
            size="small"
            onClick={() => {
              setShowAllAccounts(true);
              setSelectedGroup("");
              fetchUnscannedOrders("all");
            }}
          >
            All Accounts
          </Button>

          <Box sx={{ flexGrow: 1 }} />

          <Button
            variant="contained"
            color="info"
            size="small"
            startIcon={<PictureAsPdfIcon />}
            disabled={pickedRows.length === 0 || openingPdf || directScanning}
            onClick={openSelectedPdfs}
          >
            {openingPdf ? "Opening…" : `Open Selected (${pickedRows.length})`}
          </Button>

          <Button
            variant="contained"
            color="warning"
            size="small"
            startIcon={<FactCheckIcon />}
            disabled={pickedRows.length === 0 || directScanning || openingPdf}
            onClick={directScanSelected}
          >
            {directScanning ? "Scanning…" : `Scan Direct (${pickedRows.length})`}
          </Button>

          <Button
            variant="outlined"
            color="success"
            size="small"
            startIcon={<DownloadIcon />}
            disabled={displayedData.length === 0}
            onClick={() => exportUnscannedCsv({
              nsOrderData: displayedData,
              showAllAccounts,
              accountName: selectedNsAccount,
              fileLabel: selectedGroup || selectedNsAccount || "all",
              date: selectedDate,
            })}
          >
            Excel
          </Button>
        </Box>

        <Divider style={{ margin: "6px 0" }} />

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={allChecked}
                    indeterminate={picked.size > 0 && !allChecked}
                    onChange={toggleAll}
                    disabled={visibleKeys.length === 0}
                  />
                </TableCell>
                <TableCell><b>Tracking ID</b></TableCell>
                <TableCell><b>SKU</b></TableCell>
                <TableCell><b>Quantity</b></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Array.isArray(displayedData) && displayedData.length > 0 ? (
                showAllAccounts ? (
                  displayedData.map((accountGroup, index) => (
                    <React.Fragment key={index}>
                      <TableRow>
                        <TableCell colSpan={4} style={{ backgroundColor: "#f0f0f0", fontWeight: "bold" }}>
                          {accountGroup.account_name}
                        </TableCell>
                      </TableRow>
                      {Array.isArray(accountGroup.orders) && accountGroup.orders.map((order, orderIndex) => (
                        <TableRow key={`${accountGroup.account_name}-${orderIndex}`} hover>
                          <TableCell padding="checkbox">
                            <Checkbox size="small"
                              checked={picked.has(rowKey(accountGroup.account_name, order.tracking_id))}
                              onChange={() => toggleOne(accountGroup.account_name, order.tracking_id)} />
                          </TableCell>
                          <TableCell>{order.tracking_id}</TableCell>
                          <TableCell>{order.sku}</TableCell>
                          <TableCell>{order.quantity}</TableCell>
                        </TableRow>
                      ))}
                    </React.Fragment>
                  ))
                ) : (
                  displayedData.map((order, index) => (
                    <TableRow key={index} hover>
                      <TableCell padding="checkbox">
                        <Checkbox size="small" checked={picked.has(rowKey(selectedNsAccount, order.tracking_id))}
                          onChange={() => toggleOne(selectedNsAccount, order.tracking_id)} />
                      </TableCell>
                      <TableCell>{order.tracking_id}</TableCell>
                      <TableCell>{order.sku}</TableCell>
                      <TableCell>{order.quantity}</TableCell>
                    </TableRow>
                  ))
                )
              ) : (
                <TableRow>
                  <TableCell colSpan={4} style={{ textAlign: "center", color: "gray" }}>
                    No unscanned orders available.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">Close</Button>
      </DialogActions>
    </Dialog>

    <Dialog open={directScanOpen} onClose={() => !directScanning && setDirectScanOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle>Scan Selected Orders Directly</DialogTitle>
      <DialogContent>
        <Typography sx={{ mb: 1 }}>
          Scan {pickedRows.length} selected order{pickedRows.length === 1 ? "" : "s"} for {selectedDate}?
        </Typography>
        <TextField
          fullWidth
          multiline
          minRows={2}
          label="Remarks (optional)"
          value={directScanRemarks}
          onChange={(e) => setDirectScanRemarks(e.target.value)}
          placeholder="Add a note for this account/date"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setDirectScanOpen(false)} disabled={directScanning}>Cancel</Button>
        <Button onClick={confirmDirectScan} variant="contained" color="warning" disabled={directScanning}>
          {directScanning ? "Scanning…" : "Confirm Scan"}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default NotScannedDialog;
